// UI labels and choices shared by the Dhātu lab (/grammar/kriya) and the person × number grid (/grammar/purush-vachan).
// Labels only: every Prakrit form on these pages comes from the grammar engine.

import type { Num, Person, StemClass, Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { STEM_CLASS_LABEL } from '@/prakrit/lib/grammar/labels'

/** English grammar names under the Hindi row and column labels. */
export const PERSON_EN: Record<Person, string> = {
    uttam: 'First person',
    madhyam: 'Second person',
    anya: 'Third person'
}

export const NUMBER_EN: Record<Num, string> = {
    eka: 'Singular (one)',
    bahu: 'Plural (two or more)'
}

export const NUMBER_NOTE: Record<Num, { hi: string, en: string }> = {
    eka: { hi: 'ठीक एक व्यक्ति या वस्तु', en: 'Exactly one person or thing' },
    bahu: { hi: 'दो या अधिक: प्राकृत में द्विवचन नहीं होता', en: 'Two or more: Prakrit has no dual' }
}

/** The roots shown as tiles in step 1 (common, short meanings; the rest are under "More roots"). */
export const TILE_ROOTS = [
    'पढ',
    'लिह',
    'गच्छ',
    'अस',
    'हस',
    'खा',
    'पच',
    'भण',
    'नम',
    'खेल',
    'कर',
    'पास',
    'पा',
    'ठा',
    'सुण'
]

/** The class of a root by its last sound: अकारान्त, आकारान्त, एकारान्त, ओकारान्त (अस is irregular). */
export const rootClassLabel = (root: LabRoot): { hi: string, en: string } => {
    if (root.group === 'as') return { hi: 'अनियमित धातु', en: 'irregular root' }

    const last = root.root.slice(-1)
    const cls: StemClass = last === 'ा' ? 'ā' : last === 'े' ? 'e' : last === 'ो' ? 'o' : 'a'

    return STEM_CLASS_LABEL[cls]
}

/** The tense without its voice prefix (कर्मवाच्य — वर्तमान → वर्तमान), for chips inside a voice group. */
export const baseTense = (tense: Tense): Tense => tense.replace(/^(karmani|prerak)_/, '') as Tense

export type TenseFamily = 'basic' | 'karmani' | 'prerak' | 'kriyatipatti'

export const TENSE_FAMILIES: { value: Exclude<TenseFamily, 'basic'>, hi: string, en: string }[] = [
    { value: 'karmani', hi: 'कर्मवाच्य', en: 'Passive' },
    { value: 'prerak', hi: 'प्रेरणार्थक', en: 'Causative' },
    { value: 'kriyatipatti', hi: 'क्रियातिपत्ति', en: 'Conditional' }
]

export const familyOf = (tense: Tense): TenseFamily => {
    if (tense.startsWith('karmani_')) return 'karmani'
    if (tense.startsWith('prerak_')) return 'prerak'
    if (tense === 'kriyatipatti') return 'kriyatipatti'

    return 'basic'
}
